import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { MedicalCarrierInformation } from '@/components/application/entity/MedicalCarrierInformation';
import { AncillaryCarrierInformation } from '@/components/application/entity/AncillaryCarrierInformation';
import { HealthPlans } from '@/components/application/insurance/HealthPlans';
import { MedicalEnrollmentInfo } from '@/components/application/insurance/MedicalEnrollmentInfo';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type { Application } from '@/types/database.types';
import { useEffect } from 'react';

interface CarrierInformationProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const CarrierInformation = ({ register, watch, setValue, data }: CarrierInformationProps) => {
  // Ensure data is present before processing
  const processedData = data || {};

  // Watch the new fields for offering benefits
  const offersMedicalBenefits = watch('offers_medical_benefits');
  const offersAncillaryBenefits = watch('offers_ancillary_benefits');
  const offersRetirementPlan = watch('offers_retirement_plan');
  const hasRetirementPlan = watch('has_retirement_plan');
  const retirementPlanType = watch('retirement_plan_type');
  const hasFinancialAdvisor = watch('has_financial_advisor');

  useEffect(() => {
    // Initialize the medical benefits field if it doesn't exist
    if (processedData.offers_medical_benefits === undefined || processedData.offers_medical_benefits === null) {
      setValue('offers_medical_benefits', false);
    } else {
      setValue('offers_medical_benefits', Boolean(processedData.offers_medical_benefits));
    }

    // Initialize the ancillary benefits field if it doesn't exist
    if (processedData.offers_ancillary_benefits === undefined || processedData.offers_ancillary_benefits === null) {
      setValue('offers_ancillary_benefits', false);
    } else {
      setValue('offers_ancillary_benefits', Boolean(processedData.offers_ancillary_benefits));
    }

    // Initialize the retirement plan field if it doesn't exist
    if (processedData.offers_retirement_plan === undefined || processedData.offers_retirement_plan === null) {
      setValue('offers_retirement_plan', false);
    } else {
      setValue('offers_retirement_plan', Boolean(processedData.offers_retirement_plan));
    }

    // Initialize retirement plan related fields
    if (processedData.has_retirement_plan === undefined || processedData.has_retirement_plan === null) {
      setValue('has_retirement_plan', false);
    } else {
      setValue('has_retirement_plan', Boolean(processedData.has_retirement_plan));
    }

    if (processedData.has_financial_advisor === undefined || processedData.has_financial_advisor === null) {
      setValue('has_financial_advisor', false);
    } else {
      setValue('has_financial_advisor', Boolean(processedData.has_financial_advisor));
    }
  }, [processedData, setValue]);

  return (
    <div className="space-y-10">
      <div>
        <h3 className="text-lg font-semibold mb-4">Medical Benefits</h3>
        
        {/* New slider for offering medical benefits */}
        <div className="flex items-center justify-between mb-6">
          <Label htmlFor="offers_medical_benefits" className="cursor-pointer">
            Is the organization interested in offering Medical Benefits?
          </Label>
          <Switch
            id="offers_medical_benefits"
            checked={Boolean(offersMedicalBenefits)}
            onCheckedChange={(checked) => setValue('offers_medical_benefits', Boolean(checked))}
          />
        </div>

        {/* Only show medical insurance fields when offering medical benefits */}
        {offersMedicalBenefits && (
          <>
            <MedicalCarrierInformation
              register={register}
              watch={watch}
              setValue={setValue}
              data={processedData}
            />

            {/* Health Plans section */}
            <div className="mt-8">
              <HealthPlans
                register={register}
                watch={watch}
                setValue={setValue}
                data={processedData}
              />
            </div>
            
            {/* Enrollment Information section */}
            <div className="mt-8">
              <MedicalEnrollmentInfo
                register={register}
                watch={watch}
                setValue={setValue}
                data={processedData}
              />
            </div>
          </>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Ancillary Benefits</h3>
        
        {/* New slider for offering ancillary benefits */}
        <div className="flex items-center justify-between mb-6">
          <Label htmlFor="offers_ancillary_benefits" className="cursor-pointer">
            Is the organization interested in offering Ancillary Benefits?
          </Label>
          <Switch
            id="offers_ancillary_benefits"
            checked={Boolean(offersAncillaryBenefits)}
            onCheckedChange={(checked) => setValue('offers_ancillary_benefits', Boolean(checked))}
          />
        </div>

        {/* Only show ancillary benefits fields when offering ancillary benefits */}
        {offersAncillaryBenefits && (
          <AncillaryCarrierInformation
            register={register}
            watch={watch}
            setValue={setValue}
            data={processedData}
          />
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Retirement Plan</h3>
        
        {/* New slider for offering retirement plan */}
        <div className="flex items-center justify-between mb-6">
          <Label htmlFor="offers_retirement_plan" className="cursor-pointer">
            Is the organization interested in offering a Retirement Plan?
          </Label>
          <Switch
            id="offers_retirement_plan"
            checked={Boolean(offersRetirementPlan)}
            onCheckedChange={(checked) => setValue('offers_retirement_plan', Boolean(checked))}
          />
        </div>

        {/* Show retirement plan fields when offering retirement plan */}
        {offersRetirementPlan && (
          <div className="space-y-6">
            {/* Does organization currently offer a retirement plan */}
            <div className="flex items-center justify-between">
              <Label htmlFor="has_retirement_plan" className="cursor-pointer">
                Does the organization currently offer a retirement plan?
              </Label>
              <Switch
                id="has_retirement_plan"
                checked={Boolean(hasRetirementPlan)}
                onCheckedChange={(checked) => setValue('has_retirement_plan', Boolean(checked))}
              />
            </div>

            {/* Show additional fields when they have a retirement plan */}
            {hasRetirementPlan && (
              <div className="space-y-6 ml-6">
                {/* Retirement plan type dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="retirement_plan_type">What kind of retirement plan is offered today?</Label>
                  <Select
                    value={retirementPlanType || ''}
                    onValueChange={(value) => setValue('retirement_plan_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select retirement plan type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="401k">401(k)</SelectItem>
                      <SelectItem value="403b">403(b)</SelectItem>
                      <SelectItem value="simple_ira">SIMPLE IRA</SelectItem>
                      <SelectItem value="sep_ira">SEP IRA</SelectItem>
                      <SelectItem value="pension_plan">Pension Plan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Financial advisor question */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="has_financial_advisor" className="cursor-pointer">
                    Does this plan currently go through a Financial Advisor?
                  </Label>
                  <Switch
                    id="has_financial_advisor"
                    checked={Boolean(hasFinancialAdvisor)}
                    onCheckedChange={(checked) => setValue('has_financial_advisor', Boolean(checked))}
                  />
                </div>

                {/* Financial advisor details when they have one */}
                {hasFinancialAdvisor && (
                  <div className="space-y-4 ml-6">
                    {/* Informational note */}
                    <div className="bg-primary/5 p-3 rounded-lg border border-primary/10">
                      <p className="text-sm text-muted-foreground">
                        If you'd like to maintain that relationship, please provide their contact information below:
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="financial_advisor_firm_name">Firm Name</Label>
                      <Input
                        id="financial_advisor_firm_name"
                        {...register('financial_advisor_firm_name')}
                        placeholder="Enter firm name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="financial_advisor_contact_name">Contact Name</Label>
                      <Input
                        id="financial_advisor_contact_name"
                        {...register('financial_advisor_contact_name')}
                        placeholder="Enter contact name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="financial_advisor_email">Email Address</Label>
                      <Input
                        id="financial_advisor_email"
                        type="email"
                        {...register('financial_advisor_email')}
                        placeholder="Enter email address"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="financial_advisor_phone">Phone Number</Label>
                      <Input
                        id="financial_advisor_phone"
                        type="tel"
                        {...register('financial_advisor_phone')}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
